import { User, Mail, Lock } from 'lucide-react'
import { useEffect } from 'react'
import {
  type UseFormRegister,
  type UseFormSetValue,
  type UseFormWatch,
  type FieldErrors,
} from 'react-hook-form'

import { type UserFormInput } from '@/lib/validation/user-schemas'

interface BasicInfoSectionProps {
  register: UseFormRegister<UserFormInput>
  setValue: UseFormSetValue<UserFormInput>
  watch: UseFormWatch<UserFormInput>
  errors: FieldErrors<UserFormInput>
  isEditMode?: boolean
}

export function BasicInfoSection({
  register,
  setValue,
  watch,
  errors,
  isEditMode = false,
}: BasicInfoSectionProps) {
  const email = watch('basicInfo.email')

  useEffect(() => {
    if (!isEditMode && email) {
      setValue('basicInfo.username', email)
    }
  }, [email, isEditMode, setValue])

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm">
      <div className="p-6 space-y-6">
        <div className="pb-4 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">
            Basic Information
          </h3>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            User identity and authentication details
          </p>
        </div>

        <div className="space-y-4">
          <EmailField register={register} errors={errors} isEditMode={isEditMode} />
          <UsernameField register={register} errors={errors} />
          <NameFields register={register} errors={errors} />
          <PasswordField register={register} errors={errors} isEditMode={isEditMode} />
          {!isEditMode && <TemporaryPasswordCheckbox register={register} />}
        </div>
      </div>
    </div>
  )
}

const INPUT_CLASS =
  'block w-full px-3.5 py-2.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white text-gray-900 dark:bg-gray-900 dark:text-gray-100 transition-all'

type FormErrors = FieldErrors<UserFormInput>
type FormRegister = UseFormRegister<UserFormInput>

const FieldError = ({ message }: { message: string | undefined }) => {
  if (!message) return null
  return <p className="mt-1.5 text-sm text-red-600 dark:text-red-400">{message}</p>
}

const EmailField = ({
  register,
  errors,
  isEditMode,
}: {
  register: FormRegister
  errors: FormErrors
  isEditMode: boolean
}) => (
  <div>
    <label
      htmlFor="email"
      className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
    >
      Email address{' '}
      {isEditMode && (
        <span className="text-xs text-gray-500 dark:text-gray-400">(cannot be changed)</span>
      )}
    </label>
    <div className="relative">
      <div className="absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none text-gray-500 dark:text-gray-400">
        <Mail className="w-4 h-4" />
      </div>
      <input
        {...register('basicInfo.email')}
        type="email"
        id="email"
        disabled={isEditMode}
        className={`${INPUT_CLASS} pl-10 disabled:bg-gray-50 dark:disabled:bg-gray-800 disabled:cursor-not-allowed disabled:opacity-60`}
        placeholder="user@example.com"
      />
    </div>
    <FieldError message={errors.basicInfo?.email?.message} />
  </div>
)

const UsernameField = ({ register, errors }: { register: FormRegister; errors: FormErrors }) => (
  <div>
    <label
      htmlFor="username"
      className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
    >
      Username{' '}
      <span className="text-xs text-gray-500 dark:text-gray-400">(auto-populated from email)</span>
    </label>
    <div className="relative">
      <div className="absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none text-gray-500 dark:text-gray-400">
        <User className="w-4 h-4" />
      </div>
      <input
        {...register('basicInfo.username')}
        type="text"
        id="username"
        readOnly
        className="block w-full pl-10 pr-3.5 py-2.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-gray-50 text-gray-900 dark:bg-gray-800 dark:text-gray-100 transition-all cursor-default opacity-75"
        placeholder="user@example.com"
      />
    </div>
    <FieldError message={errors.basicInfo?.username?.message} />
  </div>
)

const NameFields = ({ register, errors }: { register: FormRegister; errors: FormErrors }) => (
  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
    <div>
      <label
        htmlFor="firstName"
        className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
      >
        First name
      </label>
      <input
        {...register('basicInfo.firstName')}
        type="text"
        id="firstName"
        className={INPUT_CLASS}
        placeholder="John"
      />
      <FieldError message={errors.basicInfo?.firstName?.message} />
    </div>
    <div>
      <label
        htmlFor="lastName"
        className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
      >
        Last name
      </label>
      <input
        {...register('basicInfo.lastName')}
        type="text"
        id="lastName"
        className={INPUT_CLASS}
        placeholder="Doe"
      />
      <FieldError message={errors.basicInfo?.lastName?.message} />
    </div>
  </div>
)

const PasswordField = ({
  register,
  errors,
  isEditMode,
}: {
  register: FormRegister
  errors: FormErrors
  isEditMode: boolean
}) => (
  <div>
    <label
      htmlFor="password"
      className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
    >
      {isEditMode ? 'Password (leave blank to keep unchanged)' : 'Password'}
    </label>
    <div className="relative">
      <div className="absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none text-gray-500 dark:text-gray-400">
        <Lock className="w-4 h-4" />
      </div>
      <input
        {...register('basicInfo.password')}
        type="password"
        id="password"
        className={`${INPUT_CLASS} pl-10`}
        placeholder="••••••••"
      />
    </div>
    <FieldError message={errors.basicInfo?.password?.message} />
  </div>
)

const TemporaryPasswordCheckbox = ({ register }: { register: FormRegister }) => (
  <div className="flex items-center">
    <input
      {...register('basicInfo.temporaryPassword')}
      type="checkbox"
      id="temporaryPassword"
      className="h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-primary-600 focus:ring-primary-500 bg-white dark:bg-gray-900"
    />
    <label
      htmlFor="temporaryPassword"
      className="ml-2 block text-sm text-gray-700 dark:text-gray-300"
    >
      Require password change on first login
    </label>
  </div>
)
